import { useState } from "react";
import { ChevronDown, Calculator, Search, Check, Clock } from "lucide-react";
import { BRANDS, modelsOf, repairsOf, POPULAR } from "./priceData";
import { PickerSheet } from "./PickerSheet";
import { PriceResult } from "./PriceResult";

const Trigger = ({ label, value, placeholder, disabled, onClick, testid }) => (
    <div className="flex-1 min-w-0">
        <span className="block mb-2 text-xs uppercase tracking-[0.2em] text-white/50 font-semibold">{label}</span>
        <button type="button" disabled={disabled} onClick={onClick} data-testid={testid}
            className="w-full flex items-center justify-between gap-3 rounded-xl border border-white/15 bg-white/[0.04] px-4 py-4 text-left text-sm transition-colors hover:border-brand disabled:opacity-40 disabled:hover:border-white/15">
            <span className={`truncate ${value ? "text-white font-medium" : "text-white/50"}`}>{value || placeholder}</span>
            <ChevronDown size={16} className="shrink-0 text-white/60" />
        </button>
    </div>
);

export const PriceCalculator = () => {
    const [brand, setBrand] = useState("");
    const [model, setModel] = useState("");
    const [repairName, setRepairName] = useState("");
    const [picker, setPicker] = useState(null);

    const repairs = repairsOf(brand, model);
    const repair = repairs.find((r) => r.name === repairName);

    const pick = (value) => {
        if (picker === "brand") { setBrand(value); setModel(""); setRepairName(""); }
        if (picker === "model") { setModel(value); setRepairName(""); }
        if (picker === "repair") setRepairName(value);
        setPicker(null);
    };

    const sheet = {
        brand: { title: "Marke wählen", items: BRANDS },
        model: { title: "Modell wählen", items: modelsOf(brand) },
        repair: { title: "Reparatur wählen", items: repairs.map((r) => r.name) },
    }[picker];

    return (
        <section id="preisrechner" className="relative overflow-hidden bg-[#0A0A0A] text-white py-20 md:py-28" data-testid="price-calculator">
            <div className="pointer-events-none absolute -top-40 -left-40 h-[420px] w-[420px] rounded-full bg-brand/30 blur-[120px]" />
            <div className="pointer-events-none absolute -bottom-40 -right-40 h-[420px] w-[420px] rounded-full bg-brand/20 blur-[120px]" />
            <div className="relative max-w-[900px] mx-auto px-6">
                <div className="text-center">
                    <span className="inline-flex items-center gap-2 rounded-full border border-brand/40 bg-brand/10 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-brand font-semibold"><Calculator size={14} /> Preisrechner</span>
                    <h2 className="mt-5 font-display text-4xl md:text-6xl tracking-tight leading-[1]">Was kostet Ihre Reparatur?</h2>
                    <p className="mt-4 text-white/60 text-base md:text-lg">Marke, Modell und Reparatur wählen – schon steht Ihr Preis da. Ohne Anmeldung, ohne versteckte Kosten.</p>
                </div>

                <div className="mt-10 relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-6 md:p-8 shadow-2xl">
                    <div className="absolute inset-x-0 top-0 h-1 rounded-t-2xl bg-brand" />
                    <div className="flex flex-wrap items-center gap-2 mb-6">
                        <span className="text-xs uppercase tracking-[0.2em] text-white/50 mr-1">Beliebt:</span>
                        {POPULAR.map((p) => (
                            <button key={p.label} type="button" onClick={() => { setBrand(p.brand); setModel(p.model); setRepairName(p.repair); }}
                                className="rounded-full border border-white/15 px-3 py-1.5 text-xs text-white/80 hover:border-brand hover:text-white transition-colors"
                                data-testid={`price-chip-${p.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{p.label}</button>
                        ))}
                    </div>
                    <div className="flex flex-col md:flex-row gap-4">
                        <Trigger label="Marke" value={brand} placeholder="Marke wählen…" onClick={() => setPicker("brand")} testid="price-brand-trigger" />
                        <Trigger label="Modell" value={model} placeholder={brand ? "Modell wählen…" : "Erst Marke wählen"} disabled={!brand} onClick={() => setPicker("model")} testid="price-model-trigger" />
                        <Trigger label="Reparatur" value={repairName} placeholder={model ? "Reparatur wählen…" : "Erst Modell wählen"} disabled={!model} onClick={() => setPicker("repair")} testid="price-repair-trigger" />
                    </div>

                    {repair ? <PriceResult model={model} repair={repair} /> : (
                        <div className="mt-8 rounded-xl border border-dashed border-white/15 p-6 flex flex-col md:flex-row md:items-center gap-4" data-testid="price-empty">
                            <div className="h-12 w-12 shrink-0 rounded-full bg-brand/15 text-brand flex items-center justify-center"><Search size={20} /></div>
                            <div className="flex-1">
                                <p className="font-semibold">Ihr Preis erscheint hier.</p>
                                <p className="text-sm text-white/50">Marke, Modell und Reparatur auswählen – oder oben auf eine beliebte Reparatur tippen.</p>
                            </div>
                            <div className="flex flex-wrap gap-3 text-xs text-white/60">
                                <span className="inline-flex items-center gap-1"><Check size={13} className="text-brand" /> Festpreis</span>
                                <span className="inline-flex items-center gap-1"><Clock size={13} className="text-brand" /> Meist 60–120 Min</span>
                            </div>
                        </div>
                    )}
                </div>
                <div className="mt-6 text-center">
                    <a href="#pr-alle-preise" onClick={(e) => { e.preventDefault(); document.getElementById("pr-alle-preise")?.scrollIntoView({ behavior: "smooth" }); }} className="text-sm text-white/60 underline hover:text-white transition-colors" data-testid="price-to-list">Zur vollständigen Preisliste ↓</a>
                </div>
            </div>
            {sheet && <PickerSheet title={sheet.title} items={sheet.items} onSelect={pick} onClose={() => setPicker(null)} />}
        </section>
    );
};
